import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate, useForm } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  NumberInput,
  RegularModal as BaseRegularModal,
  UserSelect,
} from '@/components_deprecated'
import VehicleSelect from '@/components/vehicle-select'
import { differenceInWeeks, startOfDay } from 'date-fns'
import type {
  VehicleExpirationNode,
  VehicleExpirationTypeShallow,
} from '@/modules/vehicles'
import { Input, Field, Form } from '@/modules/users/user-settings/components'
import type {
  EditVehicleExpirationForm,
  EditVehicleExpirationPromptResolve,
} from '@/modules/vehicles/types'
import styled from 'styled-components'
import { useState } from 'react'
import { VehicleExpirationTypeSelect } from './VehicleExpirationTypeSelect'

const RegularModal = styled(BaseRegularModal)`
  box-shadow: ${props => props.theme.shadow.default};
`

const Wrapper = styled.div`
  padding: 1rem 1rem;
  display: flex;
  justify-content: space-between;
`

interface EditVehicleExpirationModalProps {
  vehicleExpiration?: VehicleExpirationNode
  /**
   * Whether to ask which vehicle the expiration belongs to. On for the
   * fleet-wide expirations page, off on a vehicle's own tab, which supplies it
   * itself.
   */
  askForVehicle?: boolean
  onSubmit: PromptResolve<EditVehicleExpirationPromptResolve | null>
}

export const EditVehicleExpirationModal: React.FC<
  EditVehicleExpirationModalProps
> = ({ vehicleExpiration, askForVehicle = false, onSubmit }) => {
  const translations = useTranslate({
    save: 'common.save',
    type: 'common.type',
    vehicle: 'common.vehicle',
    newExpirationDate: 'vehicles.new-expiration-date',
    editExpirationDate: 'vehicles.edit-expiration-date',
    description: 'common.description',
    hasNotification: 'settings.has-notification',
    expirationDate: 'common.expires-at',
    notificationTime: 'vehicles.expiration.notification-time',
    responsibleUser: 'common.responsible-user',
    validation: {
      required: 'common.required',
    },
  })
  const editing = typeof vehicleExpiration !== 'undefined'
  const [hasNotification, setHasNotification] = useState(
    !!vehicleExpiration?.notificationTime || false
  )

  const expirationDate = vehicleExpiration?.expirationDate
    ? new Date(vehicleExpiration.expirationDate)
    : new Date()

  const notificationTime =
    !vehicleExpiration?.expirationDate || !vehicleExpiration?.notificationTime
      ? 1
      : differenceInWeeks(
          new Date(vehicleExpiration.expirationDate),
          startOfDay(new Date(vehicleExpiration.notificationTime))
        )

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    submitHandler: submit,
    formChangeHandler: handler,
  } = useForm<EditVehicleExpirationForm>({
    values: {
      description: vehicleExpiration?.description ?? '',
      expirationDate,
      notificationTime,
      expirationType: vehicleExpiration?.expirationType?.id || null,
      responsibleUser: vehicleExpiration?.responsibleUser?.id || null,
      vehicle: vehicleExpiration?.vehicle?.id ?? null,
    },
    validators: {
      expirationDate: value =>
        value ? null : translations.validation.required,
      expirationType: val => (val ? null : translations.validation.required),
      vehicle: val =>
        !askForVehicle || val ? null : translations.validation.required,
    },
    config: {
      initAsInvalid: true,
    },
  })

  function handleChangeExpirationType(
    id: string,
    _category: VehicleExpirationTypeShallow | null
  ) {
    handler('expirationType')(id)
  }

  return (
    <RegularModal
      title={
        editing
          ? translations.editExpirationDate
          : translations.newExpirationDate
      }
      width="480px"
      submitText={translations.save}
      submitDisabled={!formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={submit(form => onSubmit({ form, hasNotification }))}
    >
      <Form>
        {askForVehicle && (
          <Field>
            <label>{translations.vehicle}</label>
            <VehicleSelect
              value={form.vehicle ?? ''}
              className="mt-2"
              clearable
              onChange={vehicle => handler('vehicle')(vehicle?.id ?? null)}
            />
            {errors.vehicle && (
              <span className="text-sm text-red-600">{errors.vehicle}</span>
            )}
          </Field>
        )}

        <Field>
          <label>{translations.expirationDate}</label>
          <DatePicker
            value={form.expirationDate}
            fullWidth
            onChange={handler('expirationDate')}
          />
        </Field>

        <Field>
          <label>{translations.type}</label>
          <VehicleExpirationTypeSelect
            fullWidth
            value={form.expirationType}
            onChange={handleChangeExpirationType}
          />
        </Field>

        <Field>
          <label>{translations.description}</label>
          <Input
            value={form.description}
            error={errors.description}
            fullWidth
            autoFocus
            onChange={handler('description')}
          />
        </Field>

        <Wrapper>
          <Field>
            <Checkbox
              checked={hasNotification}
              label={translations.hasNotification}
              boxSize="32px"
              onChange={setHasNotification}
            />
          </Field>
        </Wrapper>

        {hasNotification && (
          <>
            <Field>
              <label>{translations.responsibleUser}</label>
              <UserSelect
                value={form.responsibleUser}
                fullWidth
                onChange={handler('responsibleUser')}
                nullable
              />
            </Field>

            <Field>
              <label>{translations.notificationTime}</label>
              <NumberInput
                value={form.notificationTime ?? 1}
                min={0}
                fullWidth
                onChange={handler('notificationTime')}
              />
            </Field>
          </>
        )}
      </Form>
    </RegularModal>
  )
}
